/**
 * The run's ledger: provider usage and cost against the analysis targets,
 * the Owner's review history, and the technical attempt and stage record.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import type {
  AnalysisArtifactSummary,
  AnalysisDay2State,
  AnalysisStageAttempt,
} from '../../api.js';
import { EmptyState, Tag } from '../../ui.js';
import { SectionHeading } from '../common.js';
import type { AnalysisRunPageCopy } from './run-page-copy.js';
import { card } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';

export function RunUsageAndHistory({
  day2,
  attempts,
  artifacts,
  copy,
}: {
  readonly day2: AnalysisDay2State;
  readonly attempts: readonly AnalysisStageAttempt[];
  readonly artifacts: readonly AnalysisArtifactSummary[];
  readonly copy: AnalysisRunPageCopy;
}): ReactNode {
  return (
    <>
      <section style={{ marginBottom: 'var(--space-lg)' }}>
        <SectionHeading>{copy.usage}</SectionHeading>
        <p style={{ color: 'var(--text-muted)', font: 'var(--type-caption)' }}>
          Analysis target: at or below $5 · acquisition hard stop: $10
        </p>
        {day2.usage.length === 0 ? (
          <EmptyState title="No provider usage recorded." compact />
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {day2.usage.map((entry) => (
              <div key={entry.id} style={card}>
                <strong>{entry.provider}</strong> · {entry.operation}
                <p style={{ color: 'var(--text-muted)' }}>
                  {entry.itemCount ?? 'unknown'} item(s) ·{' '}
                  {entry.costMicros === null
                    ? 'cost unknown'
                    : `$${(Number(entry.costMicros) / 1_000_000).toFixed(4)} ${entry.costBasis}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={{ marginBottom: 'var(--space-lg)' }}>
        <SectionHeading>{copy.history}</SectionHeading>
        {day2.reviews.length === 0 ? (
          <EmptyState title="No review actions yet." compact />
        ) : (
          day2.reviews.map((review) => (
            <div key={review.id} style={{ ...card, marginBottom: 8 }}>
              <Tag tone={toneFor(review.decision)}>{review.decision}</Tag>{' '}
              {review.reason ?? 'No note'}
            </div>
          ))
        )}
      </section>

      <details>
        <summary>
          Technical run history ({attempts.length} attempts, {artifacts.length}{' '}
          artifacts)
        </summary>
        <ol>
          {attempts.map((attempt) => (
            <li key={attempt.id}>
              {attempt.stage} · {attempt.executionState}
            </li>
          ))}
        </ol>
        <ol>
          {ANALYSIS_STAGES.map((stage) => (
            <li key={stage}>{stage}</li>
          ))}
        </ol>
      </details>
    </>
  );
}
